import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Alarm, Plus, Trash, FloppyDisk, ArrowBendUpRight, MagnifyingGlass, Gear, DownloadSimple } from "@phosphor-icons/react";
import { IncidentSummaryTable, CLASSIFICATIONS } from "../components/incidents/IncidentSummaryTable";
import { IncidentSummaryFields } from "../components/incidents/IncidentSummaryFields";
import { IncidentClockPanel } from "../components/incidents/IncidentClockPanel";
import { IncidentProfileChart } from "../components/incidents/IncidentProfileChart";
import { useAuth } from "../context/AuthContext";
import { SlaPill } from "../components/SlaPill";
import SlaSettingsDialog from "../components/SlaSettingsDialog";

const SOURCES = [
  ["spot_check", "Spot Check"], ["self_report", "Self-Report"], ["investigation", "Investigation"],
  ["audit", "Audit"], ["self_certification", "Self-Certification"], ["complaint", "Complaint"], ["other", "Other"],
];
const SEVERITY = [["low", "Low"], ["medium", "Medium"], ["high", "High"]];
const STATUSES = [
  ["new", "New"], ["vetting", "Vetting"], ["triaged", "Triaged"],
  ["promoted", "Promoted"], ["dismissed", "Dismissed"], ["closed", "Closed"],
];
const STATUS_STYLE = {
  new: "bg-slate-100 text-slate-700 border-slate-300",
  vetting: "bg-amber-100 text-amber-800 border-amber-300",
  triaged: "bg-blue-100 text-blue-800 border-blue-300",
  promoted: "bg-emerald-100 text-emerald-800 border-emerald-300",
  dismissed: "bg-zinc-100 text-zinc-500 border-zinc-300",
  closed: "bg-green-100 text-green-800 border-green-300",
};
const labelOf = (arr, v) => (arr.find(([k]) => k === v) || [null, v])[1];

export default function IncidentTracker() {
  const { isRole } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showSla, setShowSla] = useState(false);
  const [cls, setCls] = useState("all");
  const shown = cls === "all" ? rows : rows.filter((r) => (r.classification || "none") === cls);

  const load = () => {
    setLoading(true);
    api.get("/incidents").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    try { const { data } = await api.get(`/incidents/${id}`); setSelected(data); }
    catch { toast.error("Couldn't open that incident."); }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-5 py-8 space-y-6" data-testid="incidents-page">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-primary/10 border border-primary/20"><Alarm className="text-primary" size={22} /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold">Incident Tracker</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            The front door to Enforcement &amp; Remediation. Log a potential issue, vet and triage it, then
            promote it to a PNC record or an Investigation once it warrants a formal workflow.
          </p>
        </div>
      </div>

      <IncidentProfileChart refreshKey={rows} />

      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm text-muted-foreground" data-testid="incident-count">{shown.length} incident{shown.length === 1 ? "" : "s"}</div>
          <Select value={cls} onValueChange={setCls}>
            <SelectTrigger className="rounded-none w-64 h-9" data-testid="incident-filter-classification"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classifications</SelectItem>
              <SelectItem value="none">Unclassified</SelectItem>
              {CLASSIFICATIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-none" onClick={() => downloadBlob("/incidents/export/summary.xlsx", "incident_summary.xlsx")} disabled={!rows.length} data-testid="incident-export-xlsx"><DownloadSimple size={16} className="mr-1.5" /> Export Excel</Button>
          {isRole("admin") && <Button variant="outline" className="rounded-none" onClick={() => setShowSla(true)} data-testid="incident-sla-settings-btn"><Gear size={16} className="mr-1.5" /> SLA Settings</Button>}
          <Button className="rounded-none" onClick={() => setShowNew(true)} data-testid="incident-new-btn"><Plus size={16} className="mr-1.5" /> Log Incident</Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="incidents-empty">
          No incidents logged yet. Click <span className="font-semibold">Log Incident</span> to capture your first intake.
        </div>
      ) : (
        <IncidentSummaryTable rows={shown} labelOf={labelOf} SOURCES={SOURCES} STATUSES={STATUSES} STATUS_STYLE={STATUS_STYLE} onOpen={open} />
      )}

      {showNew && <NewIncidentDialog onClose={() => setShowNew(false)} onCreated={(rec) => { setRows((rs) => [rec, ...rs]); setShowNew(false); setSelected(rec); }} />}
      <SlaSettingsDialog open={showSla} onClose={() => setShowSla(false)} />
      {selected && <IncidentDetail record={selected} onClose={() => setSelected(null)}
        onSaved={(rec) => { setRows((rs) => rs.map((r) => (r.id === rec.id ? rec : r))); setSelected(rec); }}
        onDeleted={(id) => { setRows((rs) => rs.filter((r) => r.id !== id)); setSelected(null); }} />}
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}

function NewIncidentDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", source: "spot_check", date_discovered: "", description: "", severity: "medium" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const create = async () => {
    if (!f.title.trim()) { toast.error("Give the incident a title."); return; }
    setBusy(true);
    try { const { data } = await api.post("/incidents", f); toast.success("Incident logged."); onCreated(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't log the incident."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="incident-new-dialog">
        <DialogHeader><DialogTitle>Log Incident</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title *"><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Off-hours access flagged on EMS" className="rounded-none" data-testid="incident-new-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source">
              <Select value={f.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger className="rounded-none" data-testid="incident-new-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Severity">
              <Select value={f.severity} onValueChange={(v) => set("severity", v)}>
                <SelectTrigger className="rounded-none" data-testid="incident-new-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITY.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Date discovered"><Input type="date" value={f.date_discovered || ""} onChange={(e) => set("date_discovered", e.target.value)} className="rounded-none w-48" data-testid="incident-new-date" /></Field>
          <Field label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className="rounded-none" data-testid="incident-new-desc" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={create} disabled={busy} data-testid="incident-new-save">{busy ? "Saving…" : "Log Incident"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IncidentDetail({ record, onClose, onSaved, onDeleted }) {
  const nav = useNavigate();
  const [r, setR] = useState(record);
  const [busy, setBusy] = useState("");
  useEffect(() => { setR(record); }, [record]);
  const set = (k, v) => setR((p) => ({ ...p, [k]: v }));
  const promoted = !!r.promoted_to;

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/incidents/${r.id}`, {
        title: r.title, source: r.source, date_discovered: r.date_discovered, description: r.description,
        triage_notes: r.triage_notes, severity: r.severity, status: r.status,
        classification: r.classification, triage_date: r.triage_date, initial_review: r.initial_review,
        standard_id: r.standard_id, requirement: r.requirement, owner_id: r.owner_id || "", resolution_date: r.resolution_date,
        final_disposition: r.final_disposition, self_report_filed: !!r.self_report_filed, self_report_date: r.self_report_date,
      });
      setR(data); onSaved(data); toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed."); }
    finally { setBusy(""); }
  };

  const promote = async (target) => {
    setBusy(target);
    try {
      const { data } = await api.post(`/incidents/${r.id}/promote`, { target });
      toast.success(`Promoted to ${target === "pnc" ? "PNC" : "Investigation"}.`);
      onSaved({ ...r, status: "promoted", promoted_to: target });
      nav(data.redirect);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Promote failed."); }
    finally { setBusy(""); }
  };

  const del = async () => {
    if (!window.confirm("Delete this incident? This cannot be undone.")) return;
    try { await api.delete(`/incidents/${r.id}`); toast.success("Deleted."); onDeleted(r.id); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="incident-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <span className="truncate">{r.title}</span>
            <Badge variant="outline" className={`rounded-none text-[11px] ${STATUS_STYLE[r.status] || ""}`}>{labelOf(STATUSES, r.status)}</Badge>
            <SlaPill sla={r.sla} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Title"><Input value={r.title} onChange={(e) => set("title", e.target.value)} className="rounded-none" data-testid="incident-edit-title" disabled={promoted} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Source">
              <Select value={r.source} onValueChange={(v) => set("source", v)} disabled={promoted}>
                <SelectTrigger className="rounded-none" data-testid="incident-edit-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Severity">
              <Select value={r.severity} onValueChange={(v) => set("severity", v)} disabled={promoted}>
                <SelectTrigger className="rounded-none" data-testid="incident-edit-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITY.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={r.status} onValueChange={(v) => set("status", v)} disabled={promoted}>
                <SelectTrigger className="rounded-none" data-testid="incident-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.filter(([k]) => k !== "promoted").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Date discovered"><Input type="date" value={r.date_discovered || ""} onChange={(e) => set("date_discovered", e.target.value)} className="rounded-none w-48" data-testid="incident-edit-date" disabled={promoted} /></Field>
          <Field label="Description"><Textarea value={r.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="rounded-none" data-testid="incident-edit-desc" disabled={promoted} /></Field>
          <Field label="Initial triage"><Textarea value={r.triage_notes || ""} onChange={(e) => set("triage_notes", e.target.value)} rows={3} className="rounded-none" data-testid="incident-edit-triage" disabled={promoted} placeholder="Vetting findings, initial disposition reasoning, next steps…" /></Field>
          <IncidentClockPanel record={r} onUpdated={(rec) => { setR(rec); onSaved(rec); }} />
          <IncidentSummaryFields r={r} set={set} />
        </div>

        <div className="border border-primary/20 bg-primary/5 p-3 mt-3" data-testid="incident-promote">
          <div className="flex items-center gap-2 mb-1"><ArrowBendUpRight size={18} className="text-primary" /><h4 className="font-semibold text-sm">Promote</h4></div>
          {promoted ? (
            <div className="text-sm text-emerald-700">
              Promoted to {r.promoted_to}.{" "}
              <button className="underline font-medium" data-testid="incident-open-target"
                onClick={() => nav(r.promoted_to === "pnc" ? "/pnc-tracker" : "/investigations")}>Open the {r.promoted_to} →</button>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="rounded-none" onClick={() => promote("pnc")} disabled={!!busy} data-testid="incident-promote-pnc"><MagnifyingGlass size={14} className="mr-1.5" /> {busy === "pnc" ? "Promoting…" : "Promote to PNC"}</Button>
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => promote("investigation")} disabled={!!busy} data-testid="incident-promote-investigation">{busy === "investigation" ? "Promoting…" : "Promote to Investigation"}</Button>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-4 border-t border-border pt-3">
          <Button variant="ghost" className="rounded-none text-red-600 hover:text-red-700" onClick={del} data-testid="incident-delete"><Trash size={16} className="mr-1.5" /> Delete</Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
            <Button className="rounded-none" onClick={save} disabled={!!busy} data-testid="incident-save"><FloppyDisk size={16} className="mr-1.5" /> {busy === "save" ? "Saving…" : "Save changes"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
